import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Save, KeyRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'student', label: 'Student' },
];

const TIER_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
];

const STATUS_OPTIONS = [
  { value: 'approved', label: 'Approved (active)' },
  { value: 'pending', label: 'Pending approval' },
  { value: 'disabled', label: 'Disabled (cannot log in)' },
  { value: 'rejected', label: 'Rejected' },
];

const SelectField = ({ label, value, onChange, options, testid, hint, includeNone }) => (
  <div>
    <label className="block text-xs text-[#5A6577] mb-1">{label}</label>
    <select
      value={value}
      onChange={onChange}
      className="w-full px-3 py-2 border border-[#CBD5E0] rounded-md text-sm bg-white"
      data-testid={testid}
    >
      {includeNone && <option value="">None</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    {hint && <p className="text-[11px] text-[#A0AEC0] mt-1">{hint}</p>}
  </div>
);

export default function EditUserModal({ user, token, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || '',
    role: user.role || 'teacher',
    subscription_tier: user.subscription_tier || 'free',
    status: user.status || 'approved',
    grade_level: user.grade_level || '',
  });
  const [saving, setSaving] = useState(false);
  const [resetSending, setResetSending] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        role: form.role,
        subscription_tier: form.subscription_tier,
        status: form.status,
        grade_level: form.grade_level ? parseInt(form.grade_level, 10) : null,
      };
      const res = await axios.put(`${API}/admin/users/${user.id}`, payload, { headers });
      toast.success('User updated');
      onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReset = async () => {
    setResetSending(true);
    try {
      await axios.post(`${API}/admin/users/${user.id}/send-password-reset`, {}, { headers });
      toast.success(`Reset email sent to ${user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset email');
    } finally {
      setResetSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-user-modal">
      <Card className="w-full max-w-md bg-white border-[#EBE7E0]">
        <div className="p-5 border-b border-[#EBE7E0] flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Edit user</p>
            <h2 className="font-serif-display text-lg text-[#14213D] truncate max-w-[280px]">{user.name}</h2>
            <p className="text-xs text-[#5A6577] truncate max-w-[280px]">{user.email}</p>
          </div>
          <button onClick={onClose} className="text-[#A0AEC0] hover:text-[#14213D] p-1" data-testid="edit-user-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-[#5A6577] mb-1">Full name</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-[#CBD5E0] rounded-md text-sm focus:ring-amber-500 focus:border-[#D4AF37]"
              data-testid="edit-user-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              options={ROLE_OPTIONS}
              testid="edit-user-role"
            />
            <SelectField
              label="Subscription"
              value={form.subscription_tier}
              onChange={(e) => setForm({ ...form, subscription_tier: e.target.value })}
              options={TIER_OPTIONS}
              testid="edit-user-tier"
            />
          </div>

          <SelectField
            label="Account status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            options={STATUS_OPTIONS}
            testid="edit-user-status"
            hint="Disabled or rejected users cannot log in."
          />

          {form.role === 'student' && (
            <SelectField
              label="Grade level"
              value={form.grade_level}
              onChange={(e) => setForm({ ...form, grade_level: e.target.value })}
              options={[7, 8, 9, 10, 11, 12].map((g) => ({ value: g, label: `Grade ${g}` }))}
              testid="edit-user-grade"
              includeNone
            />
          )}

          <div className="pt-3 border-t border-[#EBE7E0]">
            <Button
              type="button"
              variant="outline"
              className="w-full border-[#D4AF37] text-[#14213D] hover:bg-[#FFF8E7] text-sm"
              onClick={handleSendReset}
              disabled={resetSending}
              data-testid="edit-user-send-reset"
            >
              {resetSending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <><KeyRound className="h-4 w-4 mr-2" /> Send password reset email</>
              )}
            </Button>
            <p className="text-[11px] text-[#A0AEC0] mt-1.5 text-center">
              Emails a 24h reset link. Doesn&apos;t apply to Google sign-in accounts.
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-[#CBD5E0] text-[#5A6577] text-sm"
              onClick={onClose}
              data-testid="edit-user-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#14213D] hover:bg-[#0F1A2E] text-[#FDFBF7] text-sm"
              disabled={saving}
              data-testid="edit-user-save"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Save className="h-4 w-4 mr-2" /> Save changes</>
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
